/**
 * RegionSelector — ASTIIV BASGAT's canonical region-choice gate.
 *
 * Per product mandate (2026-02-27): every visitor must explicitly choose
 * a region before entering ANY portal (User Login, Sign Up, Partner Login,
 * Apply for Partnership, etc.). The platform does NOT remember the
 * previous choice — no localStorage, no cookies, no IP-based redirect.
 *
 * Usage:
 *   <RegionSelector
 *     title="Sign In"
 *     subtitle="Choose your region to continue"
 *     buildHref={(code) => code === 'india' ? '/in/login' : '/uae/login'}
 *     bottomNote={<>New?  <Link to="/signup">Create Account</Link></>}
 *   />
 *
 * The component is intentionally **stateless** — clicking a card
 * triggers `navigate(buildHref(code))` and writes nothing to storage.
 *
 * Scalable: to add Singapore/UK/USA later, append entries to the
 * `REGIONS` array. The visual treatment (OS-independent ISO-code badge
 * + region-coloured gradient) keeps every card consistent.
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Logo } from '@/components/Logo';

const REGIONS = [
  {
    code: 'uae',
    label: 'UAE',
    iso: 'AE',
    currency: 'AED',
    tagline: 'Dubai · Abu Dhabi · Sharjah',
    accent: 'from-emerald-500/20 via-emerald-500/5 to-transparent',
    ring: 'ring-emerald-500/30',
  },
  {
    code: 'india',
    label: 'India',
    iso: 'IN',
    currency: 'INR',
    tagline: 'Mumbai · Delhi · Bengaluru · Hyderabad',
    accent: 'from-orange-500/25 via-amber-500/5 to-transparent',
    ring: 'ring-orange-500/30',
  },
  // Future regions: add here. Eg.
  //   { code: 'sg', label: 'Singapore', iso: 'SG', currency: 'SGD', ... }
];

export const RegionSelector = ({
  title = 'Select Your Region',
  subtitle = 'Choose a country to continue',
  buildHref,
  bottomNote = null,
  testIdPrefix = 'region',
}) => {
  const navigate = useNavigate();

  const handlePick = (code) => {
    if (typeof buildHref !== 'function') return;
    const href = buildHref(code);
    // Intentionally do NOT persist the choice anywhere — every entry
    // must re-ask. This is a deliberate ASTIIV BASGAT product invariant.
    navigate(href);
  };

  return (
    <div className="min-h-screen bg-obsidian text-text-primary flex items-center justify-center p-4 relative">
      {/* Subtle ambient background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-2xl"
        data-testid={`${testIdPrefix}-selector`}
      >
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <Logo to="/" size="md" wordmarkClassName="text-gold" testId={`${testIdPrefix}-logo`} />
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-semibold mb-2">{title}</h1>
          <p className="text-text-secondary text-sm">{subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {REGIONS.map((r) => (
            <button
              key={r.code}
              type="button"
              onClick={() => handlePick(r.code)}
              className="group glass-card text-left p-7 transition-all hover:border-gold/40 hover:shadow-[0_8px_40px_rgba(212,175,55,0.12)] relative overflow-hidden"
              data-testid={`${testIdPrefix}-${r.code}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${r.accent} opacity-50 pointer-events-none`} />
              <div className="relative">
                <div className="flex items-start justify-between mb-5">
                  <span
                    className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/5 ring-1 ${r.ring} font-playfair text-2xl font-bold text-gold tracking-wider`}
                    aria-label={r.label}
                  >
                    {r.iso}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-text-secondary bg-white/5 ring-1 ring-white/10 px-2 py-0.5 rounded-full">
                    {r.currency}
                  </span>
                </div>
                <h3 className="font-playfair text-2xl font-semibold mb-1">{r.label}</h3>
                <p className="text-text-secondary text-sm mb-4">{r.tagline}</p>
                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-gold group-hover:gap-2.5 transition-all">
                  Continue <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </button>
          ))}
        </div>

        {bottomNote && (
          <div className="text-center text-text-secondary text-xs mt-8">{bottomNote}</div>
        )}
      </motion.div>
    </div>
  );
};

export default RegionSelector;
